import React, { useMemo } from 'react';

import {
  Box,
  Typography
} from '@mui/material';
import {
  AddCircleOutline,
  Cancel,
  DeleteForever,
  RemoveCircleOutline,
} from '@mui/icons-material';
import HasPermission from '../../contexts/PermissionContext';
import GreenButton from '../buttons/GreenButton';
import SecondaryButton from '../buttons/SecondaryButton';
import ExpandedDropDown from '../dropdown/ExpandedDropDown';
import ExpandedSwitch from '../switch/ExpandedSwitch';
import ExpandedTextField from '../display/ExpandedTextField';
import SettingsDataGrid from '../datagrid/SettingsDataGrid';

function SettingsComponent(props) {
    // NOTE: This relies heavily on recursion
    //   Comments written to help clarify why behaviour functions the way it does
    const { label, data, onChange, dotNotatedKey, openAddKeyDialog, setOpenAddKeyDialog, parentKey, setAddKeyParentKey, editable_key_item = false} = props
    
    const handleChange = (key, value) => {
        onChange({ ...data, [key]: value });
    };
    
    // When using the add-key function, get the key of the parent dictionary of this settings item
    //   and pass back to SettingsView so that it can pass to AddSettingsKeyDialogue
    //   so we know which dict in the settings dict to add the key to
    // Parent key should be generated automatically by this component, not set by devs when using function
    const clickAddKey = () => {
      setAddKeyParentKey(parentKey)
      setOpenAddKeyDialog(true)
    }

    const clickDeleteKey = (arg) => {
      // The placement of the clickDeleteKey function CALL in the recursive structure means
      //   var data in this function will always contain the parent dict
      //   and arg will always contain the key being deleted
      delete data[arg]
      onChange({...data})
    }

    const isObject = (x) => Object.prototype.toString.call(x) === "[object Object]" && x != null;
    const isArray = (x) => Object.prototype.toString.call(x) === "[object Array]" && x != null;
    const isString = (x) => Object.prototype.toString.call(x) === "[object String]" && x != null;
    const isBool = (x) => Object.prototype.toString.call(x) === "[object Boolean]" && x != null;

    return useMemo(() => (
      <Box>
            {Object.entries(data).map(([k, v], index) => {
              // Flatten the object's key into a dot notated string
              const fullKey = dotNotatedKey  ? `${dotNotatedKey}.${k}` : k
              return (
                // Check if the current object is to be rendered or hidden
                label[fullKey] && label[fullKey].hasOwnProperty("hidden") && label[fullKey]['hidden'] === true 
                ?
                  <div key={index}></div>
                :
                <Box
                  key={fullKey}
                  id={`Settings.${fullKey}`}
                >
                <HasPermission permission={label[fullKey]?.permissions ?? ""}>
                  {
                    // Render the title Typography
                    label[fullKey] && label[fullKey].hasOwnProperty("title")
                    ?
                      <Typography variant="h6" style={{ fontWeight: 'bold', color: 'white' }}>
                          {label[fullKey].title}
                      </Typography>
                    :
                      <></>
                  }
                  {
                    // Render the description
                    label[fullKey] && label[fullKey].hasOwnProperty("description")
                    ?
                      <Typography variant="body2"  style={{ color: '#abb2bf' }} paddingBottom='15px'>
                          {label[fullKey].description}
                      </Typography>
                    :
                      <></>
                  }
                  {/* RENDERING THE ACTUAL SETTING BOX */}
                  <Box display= 'flex' flexDirection='row'>
                  {
                    // Check if the value of the key is an object
                    isObject(v)
                    ?
                      (
                        // Check if the object is to be rendered as a table
                        //   TODO: table is deprecated
                        label[fullKey]?.type === 'table'
                        ?
                          <SettingsDataGrid
                            k={k}
                            rows={v}
                            label={label}
                            fullKey={fullKey}
                            handleChange={handleChange}
                          />
                        :
                          // If value is object and item not table, recursively call this component again to fill out object's keys
                          //   If the parent item is set to display editable keys, ensure each child has editable key
                          label[fullKey] && label[fullKey].hasOwnProperty("editable_keys") && label[fullKey]["editable_keys"] === true 
                          ?
                          // Container to render individual key values from dict WITH editable keys functionality
                          <Box>
                            <SettingsComponent
                              data={v}
                              label={label}
                              onChange={(newData) => onChange({ ...data, [k]: newData })}
                              dotNotatedKey={fullKey}
                              editable_key_item={true}
                              openAddKeyDialog={openAddKeyDialog}
                              setOpenAddKeyDialog={setOpenAddKeyDialog}
                              parentKey={fullKey}
                              setAddKeyParentKey={setAddKeyParentKey}
                            />
                          </Box>
                          :
                          // Container to render individual key values from dict base version
                            <SettingsComponent
                              data={v}
                              label={label}
                              onChange={(newData) => onChange({ ...data, [k]: newData })}
                              dotNotatedKey={fullKey}
                              openAddKeyDialog={openAddKeyDialog}
                              setOpenAddKeyDialog={setOpenAddKeyDialog}
                              setAddKeyParentKey={setAddKeyParentKey}
                            />
                      )
                    : isArray(v) ?
                      (
                        <Box>
                          {
                          v.every(item => isString(item)) ? (
                            <Box>
                              {v.map((item, idx) => (
                                <Box
                                    display='flex'
                                  >
                                    <ExpandedTextField
                                      label={label[fullKey]?.label || k.charAt(0).toUpperCase() + k.slice(1)}
                                      value={item}
                                      onChange={(event) => {
                                        const itemIndex = dotNotatedKey.split('.').length > 0 ? parseInt(dotNotatedKey.split('.')[dotNotatedKey.split('.').length - 1]) : 0
                                        const newArray = v.map((val, i) => (i === idx ? event.target.value : val));
                                        onChange({ ...data, [k]: newArray }, itemIndex)
                                      }}
                                      disabled={label[fullKey]?.disabled}
                                    />
                                    <SecondaryButton
                                        onClick={() => {
                                          const itemIndex = dotNotatedKey.split('.').length > 0 ? parseInt(dotNotatedKey.split('.')[dotNotatedKey.split('.').length - 1]) : 0
                                          onChange({ ...data, [k]: v.filter((_, i) => i !== idx) }, itemIndex)
                                        }}


                                        color='rgba(92, 92, 92, 1)'
                                        hoverColor='rgba(222, 155, 145, 1)'
                                        icon={<RemoveCircleOutline />}
                                    />
                                  </Box>
                              ))}
                              <SecondaryButton
                                  onClick={() => {
                                    const itemIndex = dotNotatedKey.split('.').length > 0 ? parseInt(dotNotatedKey.split('.')[dotNotatedKey.split('.').length - 1]) : 0
                                    onChange({ ...data, [k]: [...v, ""] }, itemIndex)
                                  }}


                                  color='rgba(92, 92, 92, 1)'
                                  hoverColor='rgba(255, 255, 255, 1)'
                                  icon={<AddCircleOutline sx={{ fontSize: '35px' }} />}
                              />
                            </Box>
                          )
                          : v.every(item => isObject(item)) ?
                          <Box>
                            {v.map((item, idx) =>
                              <Box
                                  key={idx}
                                  position='relative'
                                  p={2}
                                  my={1}
                                  sx={{
                                      border: '1px solid rgba(92, 92, 92, 1)',
                                      borderRadius: 3,
                                      '&:hover': {
                                          border: '1px solid rgba(199, 221, 206, 1)'
                                      },
                                      transition: 'border 0.15s ease-out',
                                  }}
                                >
                                  { idx !== 0 && <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            right: 0,
                                        }}
                                    >
                                      <SecondaryButton
                                        onClick={() => onChange({ ...data, [k]: v.filter((_, i) => i !== idx) })}
                                        color='rgba(92, 92, 92, 1)'
                                        hoverColor='rgba(222, 155, 145, 1)'
                                        icon={<Cancel />}
                                      />
                                    </Box>}
                                    <Typography style={{ textTransform: 'capitalize' }}>{`${k} ${idx+1}`}</Typography>
                                    <SettingsComponent
                                      data={item}
                                      label={label}
                                      onChange={(newData) => {
                                        // Update the array at idx with newData
                                        const updatedArray = v.map((item, id) =>
                                          id === idx ? newData : item
                                        );
                                        onChange({ ...data, [k]: updatedArray });
                                      }}
                                      dotNotatedKey={`${fullKey}.${idx}`}
                                      />
                                </Box>
                            )}
                             <SecondaryButton
                                  onClick={() => {
                                    onChange({ ...data, [k]: [...v, v[0]] })
                                  }}
                                  color='rgba(92, 92, 92, 1)'
                                  hoverColor='rgba(255, 255, 255, 1)'
                                  icon={<AddCircleOutline sx={{ fontSize: '35px' }} />}
                              />
                          </Box>

                          : null
                        }
                        </Box>
                      )
                    // Leaf nodes are the last level in an object/dictionary
                    // Check if the value of the leaf key is a boolean
                    : isBool(v) ?
                      (
                        <ExpandedSwitch
                          label={label[fullKey]?.label || k.charAt(0).toUpperCase() + k.slice(1)}
                          checked={v}
                          value={label[fullKey]?.options.hasOwnProperty(v) ? label[fullKey].options[v]: ""}
                          onChange={(event) => handleChange(k, event.target.checked)}
                        />
                      )
                    // Check if the value of the leaf key is a string
                    : isString(v) ?
                      (
                        // Check if the schema has defined the element to be rendered as a dropdown
                        label[fullKey]?.type === 'dropdown'
                        ?
                          <ExpandedDropDown
                            label={label[fullKey]?.label || k.charAt(0).toUpperCase() + k.slice(1)}
                            options={label[fullKey]?.options}
                            value={v}
                            onChange={(event) => handleChange(k, event.target.value)}
                            fullWidth='250px'
                          />
                        : 
                          // Else render as a TextField
                          <ExpandedTextField
                            label={label[fullKey]?.label || k.charAt(0).toUpperCase() + k.slice(1)}
                            value={v}
                            onChange={(event) => handleChange(k, event.target.value)}
                            disabled={label[fullKey]?.disabled}
                          />
                      )
                    :
                      <div />
                  }
                  {
                    editable_key_item  
                    ? 
                      <SecondaryButton
                        onClick={() => clickDeleteKey(k)}
                        hoverColor='red'
                        color='rgb(40 40 40)'
                        icon={<DeleteForever />}
                      /> 
                    : 
                      <></>
                  }
                  </Box>
                </HasPermission>
                </Box>
              )
            }
          )}
          {
            editable_key_item
            ?
              <Box display='flex' justifyContent={'center'}>
                <GreenButton onClick={clickAddKey}>Add</GreenButton>
              </Box>
            :
              <></>
          }
          </Box>
    ), [data, onChange]);
};

export default React.memo(SettingsComponent)